import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Loader2, History, Save, Info } from 'lucide-react';
import { toast } from 'sonner';

/**
 * LegacyDataCard — Admin card for editing a talent's historical experience
 * so veterans migrated from prior platforms aren't treated like new users.
 *
 * Props: { talentId: uuid }
 */
const LegacyDataCard = ({ talentId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    legacy_jobs_worked: 0,
    legacy_average_rating: '',
    legacy_notes: '',
    instant_payout_override: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/talents/${talentId}/legacy-data`);
      setData(res.data);
      setForm({
        legacy_jobs_worked: res.data.legacy_jobs_worked || 0,
        legacy_average_rating: res.data.legacy_average_rating ?? '',
        legacy_notes: res.data.legacy_notes || '',
        instant_payout_override: !!res.data.instant_payout_override,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load legacy data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (talentId) load(); /* eslint-disable-next-line */ }, [talentId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        legacy_jobs_worked: Number(form.legacy_jobs_worked) || 0,
        legacy_notes: form.legacy_notes || null,
        instant_payout_override: !!form.instant_payout_override,
      };
      if (form.legacy_average_rating !== '' && form.legacy_average_rating != null) {
        const r = parseFloat(form.legacy_average_rating);
        if (Number.isNaN(r) || r < 0 || r > 5) {
          toast.error('Legacy rating must be between 0 and 5');
          setSaving(false);
          return;
        }
        payload.legacy_average_rating = r;
      } else {
        payload.legacy_average_rating = null;
      }
      await api.patch(`/admin/talents/${talentId}/legacy-data`, payload);
      toast.success('Legacy data updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="legacy-data-card-loading">
        <CardContent className="p-6 text-sm text-gray-400 flex items-center">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading legacy data…
        </CardContent>
      </Card>
    );
  }

  const total = (data?.total_jobs_worked ?? 0);
  const platform = (data?.platform_jobs ?? 0);
  const legacy = Number(form.legacy_jobs_worked || 0);

  return (
    <Card className="border-amber-200" data-testid="legacy-data-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="w-4 h-4 text-amber-600" /> Legacy experience
          <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] uppercase tracking-wide ml-1">
            Admin-only
          </Badge>
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          Jobs worked prior to iEvntur. Counts toward payout eligibility, badges, and ranking so veterans aren't treated like brand-new talent.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Totals summary */}
        <div className="grid grid-cols-3 gap-2 rounded border border-gray-100 bg-gray-50 p-2 text-center text-xs">
          <div>
            <div className="font-bold text-gray-900 text-base">{platform}</div>
            <div className="text-gray-500">Platform</div>
          </div>
          <div>
            <div className="font-bold text-amber-700 text-base">{legacy}</div>
            <div className="text-gray-500">Legacy</div>
          </div>
          <div>
            <div className="font-bold text-emerald-700 text-base">{platform + legacy}</div>
            <div className="text-gray-500">Total</div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">Legacy jobs worked</label>
            <Input
              type="number" min="0"
              value={form.legacy_jobs_worked}
              onChange={(e) => setForm({ ...form, legacy_jobs_worked: e.target.value })}
              data-testid="legacy-jobs-worked-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">Legacy average rating (0–5)</label>
            <Input
              type="number" step="0.01" min="0" max="5"
              placeholder="e.g. 4.75"
              value={form.legacy_average_rating}
              onChange={(e) => setForm({ ...form, legacy_average_rating: e.target.value })}
              data-testid="legacy-rating-input"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">Legacy notes</label>
          <Textarea
            rows={2}
            placeholder="Internal notes — e.g. 'StaffConnect veteran, 3 yrs, always on time'"
            value={form.legacy_notes}
            onChange={(e) => setForm({ ...form, legacy_notes: e.target.value })}
            data-testid="legacy-notes-input"
          />
        </div>

        <div className="flex items-start justify-between gap-3 p-3 rounded border border-emerald-200 bg-emerald-50">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-emerald-800">Instant-Pay override</div>
            <p className="text-xs text-emerald-700 mt-0.5">
              Force-enable Instant Payout regardless of computed eligibility. Overpayment lock still applies.
            </p>
          </div>
          <Switch
            checked={form.instant_payout_override}
            onCheckedChange={(v) => setForm({ ...form, instant_payout_override: v })}
            className="data-[state=checked]:bg-emerald-600"
            data-testid="instant-payout-override-switch"
          />
        </div>

        {data?.last_updated_at && (
          <div className="text-[11px] text-gray-400 pt-1">
            Last updated: {new Date(data.last_updated_at).toLocaleString('en-US')}
            {data.last_updated_by ? ` · by ${data.last_updated_by}` : ''}
          </div>
        )}

        <Button
          onClick={save}
          disabled={saving}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="save-legacy-data-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save legacy data
        </Button>
      </CardContent>
    </Card>
  );
};

export default LegacyDataCard;
